/* Production-ready cart preference. No email, customer ID, or fingerprint is exposed here. */
(() => {
  const VERSION = '2026-09-25-v2';
  const ENVIRONMENT = 'production';
  const CHOICE = 'guam_post_purchase_followup_choice';
  const EVIDENCE = 'guam_post_purchase_followup_consent';
  const VERSION_KEY = 'guam_post_purchase_followup_consent_version';
  const ENVIRONMENT_KEY = 'guam_post_purchase_followup_environment';
  const REVISION = 'guam_post_purchase_followup_revision';
  const ROOT = window.Shopify?.routes?.root || '/';
  const state = { choice: 'disabled', confirmedChoice: 'disabled', confirmedEvidence: null, sequence: 0, write: Promise.resolve(), initialized: false };
  const instances = () => [...document.querySelectorAll('guam-followup-preview')];
  const validChoice = (value) => value === 'enabled' || value === 'disabled';

  function validCartEvidence(element) {
    const choice = element.dataset.cartChoice;
    if (!validChoice(choice) || element.dataset.cartEvidenceValid !== 'true' || element.dataset.guamFollowupEnvironment !== ENVIRONMENT || element.dataset.consentCopyVersion !== VERSION) return false;
    try {
      const proof = JSON.parse(element.dataset.cartEvidence || '');
      if (!proof || typeof proof !== 'object' || Array.isArray(proof) || proof.state !== choice || proof.version !== VERSION || proof.source !== 'checkout' || typeof proof.updated_at !== 'string') return false;
      const updatedAt = Date.parse(proof.updated_at);
      return Number.isFinite(updatedAt) && updatedAt >= 0 && updatedAt <= Date.now() + 300_000;
    } catch { return false; }
  }

  function initialState(element) {
    if (element.dataset.customerSignedIn === 'true' && element.dataset.customerPreference === 'disabled' && element.dataset.customerEvidenceValid === 'true') return 'disabled';
    if (validCartEvidence(element)) return element.dataset.cartChoice;
    if (validChoice(element.dataset.customerPreference) && element.dataset.customerEvidenceValid === 'true') return element.dataset.customerPreference;
    return 'disabled';
  }

  function evidence(choice) {
    return JSON.stringify({ version: VERSION, state: choice, updated_at: new Date().toISOString(), source: 'checkout' });
  }

  function syncNativeFields(choice = state.choice, proof = state.evidence || evidence(choice)) {
    instances().forEach((element) => element.querySelectorAll('.guam-followup-preview__native').forEach((input) => {
      if (input.dataset.guamFollowupNative === 'choice') input.value = choice;
      if (input.dataset.guamFollowupNative === 'evidence') input.value = proof;
      if (input.dataset.guamFollowupNative === 'version') input.value = VERSION;
      if (input.dataset.guamFollowupNative === 'environment') input.value = ENVIRONMENT;
    }));
  }

  function render() {
    instances().forEach((element) => {
      if (element.hidden) element.hidden = false;
      const checkbox = element.querySelector('.guam-followup-preview__checkbox');
      if (checkbox) {
        const checked = state.choice === 'enabled';
        if (checkbox.checked !== checked) checkbox.checked = checked;
        if (checkbox.disabled) checkbox.disabled = false;
      }
    });
  }

  async function save(choice, proof) {
    const revision = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const attributes = { [CHOICE]: choice, [EVIDENCE]: proof, [VERSION_KEY]: VERSION, [ENVIRONMENT_KEY]: ENVIRONMENT, [REVISION]: revision };
    const signal = typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function' ? AbortSignal.timeout(8000) : undefined;
    const response = await fetch(`${ROOT}cart/update.js`, { method: 'POST', credentials: 'same-origin', signal, headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ attributes }) });
    if (!response.ok) throw new Error('cart update failed');
    const updated = await response.json();
    if (updated.attributes?.[CHOICE] !== choice || updated.attributes?.[VERSION_KEY] !== VERSION || updated.attributes?.[ENVIRONMENT_KEY] !== ENVIRONMENT || updated.attributes?.[REVISION] !== revision) throw new Error('cart update did not persist');
  }

  function persist(choice) {
    const sequence = ++state.sequence;
    const proof = evidence(choice);
    state.choice = choice; state.evidence = proof;
    syncNativeFields(); render();
    // Serialize writes so Shopify receives choices in click order. Older responses
    // may confirm persistence but must never overwrite a newer visible choice.
    state.write = state.write.catch(() => {}).then(() => save(choice, proof)).then(() => {
      state.confirmedChoice = choice; state.confirmedEvidence = proof;
      if (sequence === state.sequence) { syncNativeFields(); render(); }
    }).catch(() => {
      if (sequence === state.sequence) {
        state.choice = state.confirmedChoice;
        state.evidence = state.confirmedEvidence || evidence(state.confirmedChoice);
        syncNativeFields(); render();
      }
      throw new Error('follow-up preference was not saved');
    });
    return state.write;
  }

  // Saving belongs only to checkbox interaction. Native checkout is never
  // intercepted: its existing hidden fields already contain the visible choice.
  document.addEventListener('change', (event) => {
    if (event.target.matches('guam-followup-preview .guam-followup-preview__checkbox')) {
      persist(event.target.checked ? 'enabled' : 'disabled').catch(() => {});
    }
  });

  class FollowupPreview extends HTMLElement {
    static observedAttributes = ['hidden'];
    connectedCallback() {
      if (!state.initialized) {
        state.choice = initialState(this); state.confirmedChoice = state.choice;
        state.evidence = evidence(state.choice); state.confirmedEvidence = state.evidence; state.initialized = true;
      }
      this.observer = new MutationObserver(() => render());
      this.observer.observe(this, { childList: true, subtree: true });
      syncNativeFields(); render();
    }
    disconnectedCallback() { this.observer?.disconnect(); }
    attributeChangedCallback() { queueMicrotask(() => { if (this.isConnected) render(); }); }
  }
  if (!customElements.get('guam-followup-preview')) customElements.define('guam-followup-preview', FollowupPreview);
})();
